/**
 * CMS Data Models
 *
 * Core data model definitions for the CMS integration.
 * These types represent the data structures returned by the Strapi CMS API.
 */

/**
 * Base entity interface that all CMS entities extend
 */
export interface CMSEntity {
    /** Unique identifier */
    id: number;
    /** Document identifier */
    documentId?: string;
    /** Creation timestamp */
    createdAt?: string;
    /** Last updated timestamp */
    updatedAt?: string;
    /** Timestamp when content was published */
    publishedAt?: string;
}

/**
 * Media/Image type from Strapi CMS
 */
export interface Media extends CMSEntity {
    /** Filename */
    name: string;
    /** Alternative text for accessibility */
    alternativeText: string | null;
    /** Caption for the media */
    caption: string | null;
    /** Width in pixels */
    width: number;
    /** Height in pixels */
    height: number;
    /** Format variations (thumbnails, responsive sizes) */
    formats: Record<string, any> | null;
    /** File hash */
    hash: string;
    /** File extension */
    ext: string;
    /** MIME type */
    mime: string;
    /** File size in KB */
    size: number;
    /** URL to the media file */
    url: string;
    /** URL to preview thumbnail */
    previewUrl: string | null;
    /** Storage provider (local, S3, etc.) */
    provider: string;
    /** Provider-specific metadata */
    provider_metadata: any | null;
}

/**
 * Shared Action component for links/buttons
 */
export interface Action {
    /** Unique identifier */
    id: number;
    /** URL or path for the action */
    Link: string;
    /** Text label for the action */
    Text: string;
}

/**
 * Visibility component defining content access rules
 */
export interface Visibility {
    /** Unique identifier */
    id: number;
    /** Access type: Public, Internal, or Client-specific */
    Type: 'Public' | 'Internal' | 'Client';
    /** Client identifier when Type is 'Client' */
    Client: string | null;
}

/**
 * Banner type enum for styling and semantics
 */
export enum BannerType {
    ANNOUNCEMENT = 'Announcement',
    WARNING = 'Warning',
    DANGER = 'Danger',
}

/**
 * Banner component for page headers
 */
export interface CMSBanner {
    /** Unique identifier */
    id: number;
    /** Component type */
    __component: 'home.banner';
    /** Call to action text */
    CTA: string;
    /** Tagline or subtitle */
    Tagline: string | null;
    /** Whether banner can be dismissed */
    Dismissible: boolean;
    /** Media file for banner graphic */
    Icon: string;
    /** Action for banner interaction */
    Action?: Action;
    /** Banner type for styling and semantics */
    Type?: BannerType; // Optional for backward compatibility
}

/**
 * Title component for page headers
 */
export interface Title {
    /** Unique identifier */
    id: number;
    /** Component type */
    __component: 'home.title';
    /** Main title text */
    Title: string;
    /** Subtitle text */
    Subtitle: string | null;
    /** Icon name from icon library */
    Icon: string;
    /** Alternative icon as media file */
    IconAlternate: Media | null;
}

/**
 * Card component for content sections
 */
export interface Card {
    /** Unique identifier */
    id: number;
    /** Title of the card */
    TitleText: string;
    /** Body content of the card */
    BodyText: string | null;
    /** Link URL */
    Link: string;
    /** Icon name from icon library */
    TitleIcon: string;
    /** Whether card links to nested content */
    isNested: boolean | null;
    /** Slug for nested content */
    NestedSlug: string | null;
    /** Status flag label */
    Flag: 'Beta' | 'Alpha' | 'New' | 'None' | 'Freemium' | 'Preview' | 'AI' | null;
    /** Is the card internal only? (legacy CMS; prefer featureFlag) */
    Internal: boolean | null;
    /** Clients that can access this card (legacy CMS; prefer featureFlag) */
    Clients: { ClientName?: string }[];
    /** Whether the card should be disabled (computed at runtime) */
    isDisabled?: boolean;
    /** Users that can access this card (legacy CMS) */
    Users: { Emails?: string }[];
    /** Feature flag key that must be enabled for this card to be visible */
    featureFlag?: string | null;
}

export interface InfoCard {
    Card: {
        id: number;
        Information: string;
        CTA: string;
        Link: string;
        isDismissible: boolean;
        Page: string; // The page where this info card is displayed
    };
}

/**
 * Section component containing cards
 */
export interface Section {
    /** Unique identifier */
    id: number;
    /** Section name/title */
    Name: string;
    /** Cards within this section */
    Cards: Card[];
    /** Visibility rules */
    Visibility: Visibility[];
}

/**
 * Navigation section variant with possibly null name
 */
export interface NavigationSection extends Omit<Section, 'Name'> {
    /** Section name/title (can be null in navigation pages) */
    Name: string | null;
}

/**
 * Complete Home page structure
 */
export interface HomePage {
    /** Unique identifier */
    id: number;
    /** Document identifier */
    documentId: string;
    /** Creation timestamp */
    createdAt: string;
    /** Last updated timestamp */
    updatedAt: string;
    /** Timestamp when content was published */
    publishedAt: string;
    /** Header components (Banner, Title) */
    Header: (CMSBanner | Title)[];
    /** Content sections */
    Sections: Section[];
}

/**
 * Navigation page structure
 */
export interface NavigationPage {
    /** Unique identifier */
    id?: number;
    /** Document identifier */
    documentId?: string;
    /** Creation timestamp */
    createdAt?: string;
    /** Last updated timestamp */
    updatedAt?: string;
    /** Timestamp when content was published */
    publishedAt?: string;
    /** Page slug */
    slug?: string;
    /** Header components (Banner, Title) */
    Header: (CMSBanner | Title)[];
    /** Content sections */
    Sections: NavigationSection[];
}

/**
 * Union type of all possible CMS components
 */
export type CMSComponent = CMSBanner | Title | Card | Section | NavigationSection;

/**
 * Union type of all header components
 */
export type HeaderComponent = CMSBanner | Title;

/**
 * Strapi response wrapper for single items
 */
export interface StrapiSingleResponse<T> {
    data: T;
    meta: Record<string, unknown>;
}

/**
 * Strapi response wrapper for collections
 */
export interface StrapiCollectionResponse<T> {
    data: T[];
    meta: {
        pagination: {
            page: number;
            pageSize: number;
            pageCount: number;
            total: number;
        };
    };
}
